/**
 * メール通知先選択コンポーネント
 *
 * ワークスペースメンバーの候補リストから選択、または手動入力でメールアドレスを追加する。
 */

import { X } from "lucide-react";
import type { FC } from "react";
import { useMemo, useRef, useState } from "react";
import type { NotificationRecipient } from "../../../hooks/use-matching-conditions-api";
import type { WorkspaceMember } from "../../../hooks/use-workspace-members-api";

type EmailRecipientPickerProps = {
  /** 選択済みの宛先リスト */
  recipients: NotificationRecipient[];
  /** ワークスペースメンバー候補 */
  members: WorkspaceMember[];
  /** 宛先追加 */
  onAdd: (recipient: NotificationRecipient) => void;
  /** 宛先削除 */
  onRemove: (email: string) => void;
  /** 無効化 */
  disabled?: boolean;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 20;

/**
 * メール通知先選択コンポーネント
 * @param {EmailRecipientPickerProps} props コンポーネントプロパティ
 * @return {JSX.Element} メール通知先選択UI
 */
export const EmailRecipientPicker: FC<EmailRecipientPickerProps> = ({
  recipients,
  members,
  onAdd,
  onRemove,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedEmails = useMemo(
    () => new Set(recipients.map(r => r.email)),
    [recipients],
  );

  const filteredMembers = useMemo(() => {
    const q = query.toLowerCase().trim();
    return members.filter(m => {
      if (selectedEmails.has(m.email)) return false;
      if (!q) return true;
      return (
        m.email.toLowerCase().includes(q) ||
        m.familyName.toLowerCase().includes(q) ||
        m.givenName.toLowerCase().includes(q) ||
        `${m.familyName}${m.givenName}`.toLowerCase().includes(q)
      );
    });
  }, [members, query, selectedEmails]);

  const isAtLimit = recipients.length >= MAX_RECIPIENTS;

  const handleSelectMember = (member: WorkspaceMember) => {
    if (isAtLimit) return;
    onAdd({
      email: member.email,
      userId: member.userId,
      displayName: `${member.familyName} ${member.givenName}`.trim(),
    });
    setQuery("");
    setIsOpen(false);
    setValidationError(null);
    inputRef.current?.focus();
  };

  const handleManualAdd = () => {
    const email = query.trim();
    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setValidationError("有効なメールアドレスを入力してください");
      return;
    }
    if (selectedEmails.has(email)) {
      setValidationError("このアドレスは既に追加されています");
      return;
    }
    if (isAtLimit) {
      setValidationError(`通知先は最大${MAX_RECIPIENTS}件までです`);
      return;
    }

    // メンバーに一致するか確認
    const matchedMember = members.find(
      m => m.email.toLowerCase() === email.toLowerCase(),
    );
    onAdd({
      email: matchedMember?.email ?? email,
      userId: matchedMember?.userId ?? null,
      displayName: matchedMember
        ? `${matchedMember.familyName} ${matchedMember.givenName}`.trim()
        : null,
    });
    setQuery("");
    setIsOpen(false);
    setValidationError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredMembers.length === 1) {
        handleSelectMember(filteredMembers[0]);
      } else {
        handleManualAdd();
      }
    }
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="label">
        <span className="label-text font-medium">
          通知先メールアドレス（複数可）
        </span>
        <span className="label-text-alt text-base-content/50">
          {recipients.length}/{MAX_RECIPIENTS}
        </span>
      </label>

      {/* 選択済みバッジ */}
      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {recipients.map(r => (
            <div key={r.email} className="badge badge-lg gap-1 pr-1">
              <span className="max-w-[200px] truncate text-sm">
                {r.displayName ? `${r.displayName} ` : ""}
                <span className="text-base-content/60">{r.email}</span>
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs btn-circle"
                onClick={() => onRemove(r.email)}
                disabled={disabled}
                aria-label={`${r.email} を削除`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 検索入力 + ドロップダウン */}
      {!isAtLimit && (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            className={`input input-bordered w-full ${validationError ? "input-error" : ""}`}
            placeholder="メールアドレスを検索または入力"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setIsOpen(true);
              setValidationError(null);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => {
              // ドロップダウンのクリックを先に処理するため遅延
              setTimeout(() => setIsOpen(false), 200);
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />

          {validationError && (
            <label className="label">
              <span className="label-text-alt text-error">
                {validationError}
              </span>
            </label>
          )}

          {/* ドロップダウン候補 */}
          {isOpen && (filteredMembers.length > 0 || query.trim()) && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border border-base-300 bg-base-100 shadow-lg max-h-48 overflow-y-auto">
              {filteredMembers.map(m => (
                <button
                  key={m.userId}
                  type="button"
                  className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-base-200"
                  onMouseDown={e => {
                    e.preventDefault();
                    handleSelectMember(m);
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">
                      {m.familyName} {m.givenName}
                    </div>
                    <div className="text-base-content/60 text-xs truncate">
                      {m.email}
                    </div>
                  </div>
                </button>
              ))}
              {query.trim() &&
                EMAIL_REGEX.test(query.trim()) &&
                !selectedEmails.has(query.trim()) && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-base-200 border-t border-base-200"
                    onMouseDown={e => {
                      e.preventDefault();
                      handleManualAdd();
                    }}
                  >
                    <span className="text-primary text-sm">+</span>
                    <span className="text-sm">
                      「{query.trim()}」を手動で追加
                    </span>
                  </button>
                )}
            </div>
          )}
        </div>
      )}

      {isAtLimit && (
        <p className="text-warning text-sm">
          通知先は最大{MAX_RECIPIENTS}件までです
        </p>
      )}
    </div>
  );
};
